import React from 'react';
import './Referencias.css';

/**
 * Referências de preço do orçamento de origem, para o COMPRADOR (12/08/2026).
 *
 * Decisão do Victor: mostrar **as duas** — o valor orçado no Cilia e a média de compras no
 * ERP — **deixando claro qual é qual**. São coisas diferentes: uma é quanto a oficina
 * orçou; a outra é quanto a Atual efetivamente pagou pela peça na janela configurada.
 *
 * ⚠️ Componente de tela INTERNA. Nunca usar em `views/cotacao_publica`: dar ao fornecedor a
 * âncora de preço da casa é o oposto do que a cotação existe para fazer.
 *
 * Um só componente serve o wizard, o modal da proposta recebida e a matriz de comparação —
 * o formato do rótulo não pode divergir entre as três telas.
 */

interface ReferenciasProps {
  /** Decimal do Prisma chega como string no JSON — o componente aceita os dois. */
  valorOrcado?: string | number | null;
  mediaCompras?: string | number | null;
  mediaComprasQtd?: number | null;
  /**
   * Preço online (orcamento-cilia/at-15): o último menor preço pesquisado na internet, com a
   * data — o setor decide se ainda vale (20/10/2026). `precoOnlineConferir` = o anúncio
   * provavelmente serve, mas não cita tudo.
   */
  precoOnline?: string | number | null;
  precoOnlineUrl?: string | null;
  precoOnlinePesquisadoEm?: string | null;
  precoOnlineConferir?: boolean | null;
  /** Preço da proposta em análise: quando vem, o desvio contra a média é calculado. */
  precoProposta?: number | null;
  /**
   * `linha` = uma linha só (célula de tabela); `bloco` = empilhado; `celulas` = cada
   * referência vira uma CÉLULA da grade do pai (`display: contents`), para conviver com
   * os outros campos rotulados do item — é o formato do modal de proposta recebida.
   */
  formato?: 'linha' | 'bloco' | 'celulas';
}

function numero(valor: string | number | null | undefined): number | null {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = typeof valor === 'number' ? valor : Number(valor);
  return Number.isFinite(n) ? n : null;
}

function moeda(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const Referencias: React.FC<ReferenciasProps> = ({
  valorOrcado,
  mediaCompras,
  mediaComprasQtd,
  precoOnline,
  precoOnlineUrl,
  precoOnlinePesquisadoEm,
  precoOnlineConferir,
  precoProposta,
  formato = 'linha',
}) => {
  const orcado = numero(valorOrcado);
  const media = numero(mediaCompras);
  const online = numero(precoOnline);

  // Sem nenhuma referência não há o que exibir: a origem pode simplesmente não informar
  // (sementes, futuro provider do ERP). Um "—" solto na coluna diria menos que nada.
  if (orcado === null && media === null && online === null) {
    return <span className="pc-ref-vazio">—</span>;
  }

  /*
   * Desvio contra a MÉDIA DE COMPRAS, não contra o orçado: a média é o que a casa pagou de
   * verdade, e é a base do alerta de preço do Cilia. Só aparece quando há proposta.
   */
  const desvio =
    precoProposta !== null && precoProposta !== undefined && media !== null && media > 0
      ? ((precoProposta - media) / media) * 100
      : null;

  const badgeDesvio =
    desvio === null ? null : (
      <span className={`pc-ref-desvio ${desvio > 0 ? 'pc-ref-desvio--acima' : 'pc-ref-desvio--abaixo'}`}>
        {desvio > 0 ? '+' : ''}
        {desvio.toFixed(0)}% vs. média
      </span>
    );

  /*
   * AUSÊNCIA É EXPLÍCITA (13/08/2026 — pedido do Victor): a referência que não existe vira
   * "N/A" em vez de sumir. Omitir o campo deixa o comprador sem saber se a peça não tem
   * histórico no ERP ou se a tela simplesmente não mostrou — e no modal da proposta ainda
   * abre um buraco na grade, ao lado de "Prazo" e "Marca", que estão sempre lá.
   *
   * A exceção é o caso de NENHUMA das duas (o `return` lá em cima): ali "—" já diz que não
   * há referência alguma, e nomear dois campos vazios seria ruído.
   */
  const naoInformado = (titulo: string) => (
    <span className="pc-ref-na" title={titulo}>
      N/A
    </span>
  );

  return (
    <span className={`pc-ref pc-ref--${formato}`}>
      <span className="pc-ref-item">
        <span className="pc-ref-rotulo">Orçado (Cilia)</span>
        <span className="pc-ref-valor">
          {orcado !== null
            ? moeda(orcado)
            : naoInformado('O orçamento de origem não informou o valor desta peça')}
        </span>
      </span>
      <span className="pc-ref-item">
        {/*
          Em `celulas` a quantidade de compras sai do rótulo e vai para junto do valor: o
          rótulo é a única linha que pode quebrar na grade, e o "· 2 compra(s)" o fazia
          quebrar sempre — desalinhando o valor desta célula em relação às vizinhas.
        */}
        <span className="pc-ref-rotulo">
          Média de compras (ERP)
          {media !== null && mediaComprasQtd && formato !== 'celulas'
            ? ` · ${mediaComprasQtd} compra(s)`
            : ''}
        </span>
        <span className="pc-ref-valor">
          {media === null ? (
            naoInformado('Sem compras desta peça registradas no Nasajon na janela consultada')
          ) : (
            <>
              {moeda(media)}
              {mediaComprasQtd && formato === 'celulas' ? (
                <span className="pc-ref-qtd">
                  em {mediaComprasQtd} compra{mediaComprasQtd > 1 ? 's' : ''}
                </span>
              ) : null}
              {/*
                Em `celulas` o desvio mora DENTRO da célula da média: ele é a leitura daquele
                número, e como célula própria ocuparia uma coluna da grade sem rótulo nenhum.
              */}
              {formato === 'celulas' && badgeDesvio}
            </>
          )}
        </span>
      </span>
      {/*
        Preço online (orcamento-cilia/at-15): a data vai NO RÓTULO porque é o que o comprador
        precisa para decidir se o preço ainda vale — sem ela, um preço de semanas atrás seria lido
        como de hoje.
      */}
      <span className="pc-ref-item">
        <span className="pc-ref-rotulo">
          Preço online
          {online !== null && precoOnlinePesquisadoEm
            ? ` (${new Date(precoOnlinePesquisadoEm).toLocaleDateString('pt-BR')})`
            : ''}
        </span>
        <span className="pc-ref-valor">
          {online === null ? (
            naoInformado('Peça não pesquisada online, ou a pesquisa não achou oferta')
          ) : (
            <>
              {precoOnlineUrl ? (
                <a href={precoOnlineUrl} target="_blank" rel="noopener noreferrer">
                  {moeda(online)}
                </a>
              ) : (
                moeda(online)
              )}
              {precoOnlineConferir ? (
                <span className="pc-ref-qtd" title="O anúncio provavelmente serve, mas não cita tudo (versão, cor, lado)">
                  conferir compatibilidade
                </span>
              ) : null}
            </>
          )}
        </span>
      </span>
      {formato !== 'celulas' && badgeDesvio}
    </span>
  );
};

export default Referencias;

/** O que uma linha precisa ter para as colunas de referência (item da requisição ou da cotação). */
export interface ComReferencias {
  valorOrcado?: string | number | null;
  mediaCompras?: string | number | null;
  mediaComprasQtd?: number | null;
  precoOnline?: string | number | null;
  precoOnlineUrl?: string | null;
  precoOnlinePesquisadoEm?: string | null;
  precoOnlineConferir?: boolean | null;
}

/**
 * As três referências como COLUNAS de tabela (`TabelaSistema`), uma por referência
 * (portal-cotacoes/at-14, pedido do Victor de 28/10/2026). Em tabela estreita os três pares
 * rótulo + valor numa célula só eram cortados: aqui o rótulo vai no cabeçalho, a célula leva só
 * o valor, e o detalhe (nº de compras, data da pesquisa, compatibilidade) vai no `title`.
 *
 * Ausência continua EXPLÍCITA ("N/A", 13/08/2026), pelo mesmo motivo do componente.
 */
export function colunasDeReferencias<T extends ComReferencias>(): {
  titulo: string;
  largura: number;
  texto: (item: T) => string;
  render?: (item: T) => React.ReactNode;
}[] {
  const valorOuNa = (n: number | null, titulo: string, conteudo?: React.ReactNode) =>
    n === null ? (
      <span className="pc-ref-na" title={titulo}>
        N/A
      </span>
    ) : (
      <span className="pc-ref-valor" title={titulo}>
        {conteudo ?? moeda(n)}
      </span>
    );

  return [
    {
      titulo: 'Orçado (Cilia)',
      largura: 110,
      texto: (i) => {
        const n = numero(i.valorOrcado);
        return n === null ? 'N/A' : moeda(n);
      },
      render: (i) =>
        valorOuNa(
          numero(i.valorOrcado),
          numero(i.valorOrcado) === null
            ? 'O orçamento de origem não informou o valor desta peça'
            : 'Valor unitário do orçamento da oficina',
        ),
    },
    {
      titulo: 'Média ERP',
      largura: 100,
      texto: (i) => {
        const n = numero(i.mediaCompras);
        return n === null ? 'N/A' : moeda(n);
      },
      render: (i) => {
        const n = numero(i.mediaCompras);
        return valorOuNa(
          n,
          n === null
            ? 'Sem compras desta peça registradas no Nasajon na janela consultada'
            : `Média de compras no Nasajon${i.mediaComprasQtd ? ` em ${i.mediaComprasQtd} compra(s)` : ''}`,
        );
      },
    },
    {
      titulo: 'Online',
      largura: 100,
      texto: (i) => {
        const n = numero(i.precoOnline);
        return n === null ? 'N/A' : moeda(n);
      },
      render: (i) => {
        const n = numero(i.precoOnline);
        if (n === null) return valorOuNa(null, 'Peça não pesquisada online, ou a pesquisa não achou oferta');
        // A data vai no title porque é o que decide se o preço ainda vale (orcamento-cilia/at-15).
        const titulo = [
          'Menor preço pesquisado online',
          i.precoOnlinePesquisadoEm ? `em ${new Date(i.precoOnlinePesquisadoEm).toLocaleDateString('pt-BR')}` : '',
          i.precoOnlineConferir ? '— conferir compatibilidade: o anúncio não cita tudo' : '',
        ]
          .filter(Boolean)
          .join(' ');
        const valor = `${moeda(n)}${i.precoOnlineConferir ? ' *' : ''}`;
        return valorOuNa(
          n,
          titulo,
          i.precoOnlineUrl ? (
            <a href={i.precoOnlineUrl} target="_blank" rel="noopener noreferrer">
              {valor}
            </a>
          ) : (
            valor
          ),
        );
      },
    },
  ];
}
